import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  ExternalDropZone,
  Upload,
  UploadFileInfo,
  UploadOnRemoveEvent,
  UploadOnStatusChangeEvent,
} from '@progress/kendo-react-upload';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ScanManagerErrorColumns } from 'src/assets/static/scan_manager/ScanManagerErrorTable';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import GridWrapper from 'src/components/GridWrapper';
import { UploadPdfOnly } from 'src/components/guards/PermissionsList';
import Modal from 'src/components/Modal';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import UploadFileListItem from 'src/components/UploadFileListItem';
import { EMAIL_PREFIX } from 'src/config';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import useDeviceType from 'src/hooks/useDeviceType';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import usePermission from 'src/hooks/usePermissions';
import usePortrait from 'src/hooks/usePortrait';
import useWindowSize from 'src/hooks/useWindowSize';
import useInvoiceS3UploadApi from 'src/services/useInvoiceS3UploadApi';
import useRunOcrScanApi from 'src/services/useRunOcrScanApi';
import useS3InvoiceAttach, { S3UploadInvoiceAttachRequest } from 'src/services/useS3InvoiceAttach';
import { SetState } from 'src/types/generic';
import {
  allowedOcrFileExtensions,
  PreRequiredCheck,
  ProcessedFilesState,
  S3FileValidations,
  unProcessedFiles,
} from 'src/utils/validation';

export interface S3AttachmentResponse {
  processedFiles: string[];
  unProcessed: string[];
}

export interface S3UploadRes {
  fileName: string | null;
  signedUrl: string;
  fileNameWithAttachId: string;
  attachId: number;
}

const uploadRef = React.createRef<Upload>();
const hint = (
  <span>
    <i>Drop files here to upload</i>
  </span>
);
type UploadFilesModalProps = {
  closeModal: VoidFunction;
  open: boolean;
  refreshParent: VoidFunction;
  setRefreshCompany?: SetState<boolean>;
  id?: number;
};
const InvoiceUploadFilesModal = ({
  closeModal,
  open,
  refreshParent,
  setRefreshCompany,
  id,
}: UploadFilesModalProps) => {
  const { company: selectedCompany } = useCompany();
  const { height, width } = useWindowSize();
  const { ocrScanRun } = useRunOcrScanApi();
  const { isOcrCompany, setSelectedView, getOcrRecordCount } = useInvoiceScanManager();
  const { S3Files: executeInvoiceUpload } = useInvoiceS3UploadApi();
  const { S3InvoiceAttach } = useS3InvoiceAttach(parseInt(selectedCompany.companyId), id);
  const { confirm } = useConfirm();
  const isPDFOnly = usePermission({ permissions: UploadPdfOnly });
  const [processedFileState, setProcessedFileState] = useState<ProcessedFilesState>({
    processedFiles: [],
    preRequiredCheck: { processed: [], unProcessed: [] },
  });
  const selectedCompanyName = useMemo(
    () => selectedCompany.companyName.split('(')[0],
    [selectedCompany.companyName]
  );
  const {
    value: isUploadLoading,
    setTrue: startUploadLoading,
    setFalse: endUploadLoading,
  } = useBoolean();
  const {
    value: isUploadAndScanLoading,
    setTrue: startUploadAndScanLoading,
    setFalse: endUploadAndScanLoading,
  } = useBoolean();
  const isUploadButtonDisable =
    processedFileState.preRequiredCheck.processed.length === 0 &&
    processedFileState.preRequiredCheck.unProcessed.length === 0;
  const isUnsupportedFileType =
    processedFileState.preRequiredCheck.processed.some(
      (file) => !allowedOcrFileExtensions.some((ext) => file.extension?.toLowerCase().includes(ext))
    ) || processedFileState.preRequiredCheck.unProcessed.length > 0;

  const isAllFilePdf =
    processedFileState.processedFiles.every((file) => file.extension?.toLowerCase() === '.pdf') &&
    processedFileState.preRequiredCheck.unProcessed.length === 0;

  const onSelectFilesClick = () => {
    if (uploadRef) uploadRef?.current?.actionElement?.click();
  };

  const postAttachments = useCallback(
    async (
      attachMethod: Function,
      requestData: S3UploadRes[],
      preRequiredCheck: PreRequiredCheck
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
    }> => {
      const { processed, unProcessed } = preRequiredCheck;

      if (requestData.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck };
      }
      const modifiedReqData = requestData.map((file) => {
        const fileNameParts = file.fileNameWithAttachId.split('.');
        const extension = fileNameParts.pop();
        return { ...file, fileName: `${file.fileName}.${extension}` };
      });
      const fileNames = modifiedReqData.map((file: any) => file.fileName);

      try {
        if (processed.length) {
          await attachMethod(modifiedReqData, id);
        }

        return {
          preRequiredCheck,
        };
      } catch (error) {
        const errors: unProcessedFiles[] = fileNames.map((fileName) => ({
          fileName,
          errorMessage: 'Failed to upload file',
        }));
        preRequiredCheck.processed = [];
        unProcessed.push(...errors);
        return {
          preRequiredCheck,
        };
      }
    },
    [id]
  );

  const putPresignedUrls = useCallback(
    async (
      s3response: S3UploadRes[],
      processedFiles: UploadFileInfo[],
      preRequiredCheck: PreRequiredCheck
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
      awsResponse: string[];
    }> => {
      if (processedFiles.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck, awsResponse: [] };
      }
      const modifiedReqData = s3response.map((file) => {
        const fileNameParts = file.fileNameWithAttachId.split('.');
        const extension = fileNameParts.pop();
        return { ...file, fileName: `${file.fileName}.${extension}` };
      });

      try {
        const awsResponse = await Promise.all(
          modifiedReqData.map(async (presignedUrls, index) => {
            // @ts-ignore
            const file = processedFiles[index].getRawFile();
            try {
              const response = await axios.put(presignedUrls.signedUrl, await file.arrayBuffer(), {
                headers: {
                  'Content-Type': file.type,
                },
              });
              const status = response.status === 200 ? 'success' : 'Fail';
              return status;
            } catch (error) {
              return 'Fail';
            }
          })
        );
        if (awsResponse.includes('Fail')) {
          preRequiredCheck.processed = [];
          const errors: unProcessedFiles[] = processedFiles.map((fileName) => ({
            fileName: fileName.name,
            errorMessage: 'Failed to upload file',
          }));
          preRequiredCheck.processed = [];
          preRequiredCheck.unProcessed.push(...errors);
          return { preRequiredCheck, awsResponse: ['Fail'] };
        }
        return { preRequiredCheck, awsResponse };
      } catch (error) {
        preRequiredCheck.processed = [];
        const errors: unProcessedFiles[] = processedFiles.map((fileName) => ({
          fileName: fileName.name,
          errorMessage: 'Failed to upload file',
        }));
        preRequiredCheck.processed = [];
        preRequiredCheck.unProcessed.push(...errors);
        return { preRequiredCheck, awsResponse: ['Fail'] };
      }
    },
    []
  );

  const getSignedUrls = useCallback(
    async (
      uploadMethod: Function,
      processedFiles: UploadFileInfo[],
      preRequiredCheck: PreRequiredCheck
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
      s3response: S3UploadInvoiceAttachRequest[];
    }> => {
      if (processedFiles.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck, s3response: [] };
      }
      const fileNames = processedFiles.map((file: any) => file.name);
      try {
        const s3response: S3UploadInvoiceAttachRequest[] = await uploadMethod(fileNames);
        return { preRequiredCheck, s3response };
      } catch (error) {
        const errors: unProcessedFiles[] = fileNames.map((fileName) => ({
          fileName,
          errorMessage: 'Failed to upload file',
        }));
        preRequiredCheck.processed = [];
        preRequiredCheck.unProcessed.push(...errors);
        return { preRequiredCheck, s3response: [] };
      }
    },
    []
  );

  const postScanAttachments = useCallback(
    async (
      ocrScanMethod: Function,
      requestData: S3UploadRes[],
      preRequiredCheck: PreRequiredCheck
    ): Promise<{
      preRequiredCheck: PreRequiredCheck;
    }> => {
      const { processed, unProcessed } = preRequiredCheck;

      if (requestData.length === 0) {
        console.warn('Processed files array is empty. Aborting file upload.');
        return { preRequiredCheck };
      }
      try {
        if (processed.length) {
          const attachIds = requestData.map((file) => file.attachId);
          await ocrScanMethod({
            companyId: Number(selectedCompany.companyId),
            attachmentIds: attachIds,
          });
        }
        return {
          preRequiredCheck,
        };
      } catch (error) {
        const errors: unProcessedFiles[] = requestData.map((file) => ({
          fileName: file.fileName as string,
          errorMessage: 'Failed to scan file',
        }));
        preRequiredCheck.processed = [];
        unProcessed.push(...errors);
        return {
          preRequiredCheck,
        };
      }
    },
    [selectedCompany?.companyId]
  );

  const onAdd = useCallback(
    async (e: UploadOnStatusChangeEvent) => {
      const validateFiles = await S3FileValidations(e?.newState, isPDFOnly);
      setProcessedFileState(validateFiles);
    },
    [isPDFOnly]
  );

  const handleRemoveFile = useCallback((event: UploadOnRemoveEvent) => {
    const deletedFile = event.affectedFiles[0];
    setProcessedFileState((prev) => ({
      processedFiles: prev.processedFiles.filter((file) => file.uid !== deletedFile.uid),
      preRequiredCheck: {
        processed: prev.preRequiredCheck.processed.filter((file) => file.uid !== deletedFile.uid),
        unProcessed: prev.preRequiredCheck.unProcessed.filter(
          (file) => file.file?.uid !== deletedFile.uid
        ),
      },
    }));
  }, []);

  const handleUploadAndScan = useCallback(
    async (runOcr: boolean) => {
      runOcr ? startUploadAndScanLoading() : startUploadLoading();
      const { processedFiles, preRequiredCheck } = processedFileState;
      // s3 file > s3 response, getting s3 url
      const { preRequiredCheck: getSignedUrlsPreReqCheck, s3response } = await getSignedUrls(
        executeInvoiceUpload,
        processedFiles,
        preRequiredCheck
      );
      //  s3 response > success or fail , uploading to s3 with file and url
      const { preRequiredCheck: putPresignedUrlsPreReqCheck } = await putPresignedUrls(
        s3response,
        processedFiles,
        getSignedUrlsPreReqCheck
      );

      // s3 response >  preRequiredCheck
      const { preRequiredCheck: postSignedUrlsPreReqCheck } = await postAttachments(
        S3InvoiceAttach,
        s3response,
        putPresignedUrlsPreReqCheck
      );
      let preRequiredCheckResult = postSignedUrlsPreReqCheck;
      if (runOcr) {
        const { preRequiredCheck: postScanPreReqCheck } = await postScanAttachments(
          ocrScanRun,
          s3response,
          postSignedUrlsPreReqCheck
        );
        preRequiredCheckResult = postScanPreReqCheck;
      }
      const { processed, unProcessed } = preRequiredCheckResult;
      closeModal();
      await confirm({
        title: 'File Upload',
        width: width > 700 ? '700px' : 'auto',
        height: processed.length > 0 ? 'auto' : '380px',
        content: (
          <div className="font-bold ">
            <div className="font-bold ">
              {processed.length > 0 && (
                <>
                  {processed.length > 0 ? (
                    <>
                      <span className="font-extrabold text-lg m-2 text-green-600">
                        {processed.length}
                      </span>
                      Record(s) uploaded successfully
                    </>
                  ) : (
                    ''
                  )}
                  {unProcessed?.length > 0 ? ' and' : ''}
                </>
              )}
              {unProcessed?.length > 0 && (
                <>
                  <span className="font-extrabold text-lg m-2 text-red-600">
                    {unProcessed?.length}
                  </span>
                  Record(s) have failed
                  <GridWrapper
                    className="m-2 no_Veritcal_ScrollBar"
                    columns={ScanManagerErrorColumns}
                    dataSource={unProcessed}
                    DataItemKey={'fileName'}
                    pageable={false}
                    tableTextId="Errors"
                    disableAutoResize
                  />
                </>
              )}
              {runOcr && (
                <p className="m-2 text-sm font-semibold">
                  Note: Any issues when creating the document will be flagged as "Needs Review" or
                  "Failed" in the OCR Queue tab.
                </p>
              )}
            </div>
          </div>
        ),
        cancelButtonText: 'Close',
        cancelColor: 'info',
        hasNoConfirmButton: true,
      });
      refreshParent();
      setRefreshCompany?.((prev) => !prev);
      runOcr && getOcrRecordCount();
      if (runOcr) {
        endUploadAndScanLoading();
        setSelectedView(processed.length > 0 ? 1 : 0);
      } else {
        endUploadLoading();
        setSelectedView(0);
      }
    },
    [
      startUploadAndScanLoading,
      startUploadLoading,
      processedFileState,
      getSignedUrls,
      executeInvoiceUpload,
      putPresignedUrls,
      postAttachments,
      S3InvoiceAttach,
      closeModal,
      confirm,
      refreshParent,
      setRefreshCompany,
      endUploadAndScanLoading,
      endUploadLoading,
      postScanAttachments,
      ocrScanRun,
      setSelectedView,
      getOcrRecordCount,
      width,
    ]
  );

  const userEmail = `${EMAIL_PREFIX}-${selectedCompany.companyId}@ibuyefficient.com`;

  const handleMailClick = useCallback(() => {
    const mailSubject = `Invoice Scans for ${selectedCompanyName}`;
    const mailURl = `mailto:${userEmail}?subject=${mailSubject}`;
    window.location.href = mailURl;
  }, [userEmail, selectedCompanyName]);

  const deviceType = useDeviceType();
  const isPortrait = usePortrait();

  const modalWidth = useMemo(
    () =>
      deviceType !== 'desktop'
        ? isPortrait
          ? `${width}px`
          : `${Math.min(width, 550)}px`
        : '700px',
    [deviceType, isPortrait, width]
  );

  useEffect(() => {
    let spanElement = document.querySelector(
      '#uploadInvoiceModal span[aria-hidden="true"]'
    ) as HTMLSpanElement;
    if (width <= 1024) {
      spanElement.innerText = 'Select Files or Take Picture';
    } else {
      spanElement.innerText = 'Select files...';
    }
  }, [width]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const contentHeight = useMemo(
    () => (height > 650 ? (isOcrCompany ? '480px' : '410px') : `${height - 125}px`),
    [height, isOcrCompany]
  );
  const isUploadAndScanDisabled =
    isUploadButtonDisable ||
    isUnsupportedFileType ||
    isUploadLoading ||
    (isPDFOnly && !isAllFilePdf);

  const { scanFilePopUpMessage } = useMemo(() => {
    let scanFileMessage = '';
    if (isPDFOnly && !isAllFilePdf) {
      scanFileMessage = 'OCR supports only .PDF formats.';
    } else if (isUnsupportedFileType) {
      scanFileMessage = 'OCR supports only .PDF, .JPG, .JPEG and .PNG formats.';
    } else if (isUploadButtonDisable) {
      scanFileMessage = 'Please select a file to Upload and Scan.';
    }
    return { isUnsupportedFileType, scanFilePopUpMessage: scanFileMessage };
  }, [isPDFOnly, isUnsupportedFileType, isUploadButtonDisable, isAllFilePdf]);

  return (
    <div>
      <Modal title={'Upload Files'} closeModal={closeModal} visible={open} width={modalWidth}>
        <div style={{ maxHeight: contentHeight }}>
          <div className="[&>p]:text-sm [&>p]:mb-2">
            {isOcrCompany && (
              <p className="font-semibold !mb-4">
                Average scan time 5 minutes or less for small batches. Please allow up to 4 hours
                for processing.
              </p>
            )}
            <p>Please select or drop the image files you want to upload.</p>
            {!isOcrCompany ? (
              <p>
                Alternatively, attachments can be loaded by email instead by sending them to{' '}
                <span
                  className="link text-info font-semibold cursor-pointer whitespace-nowrap"
                  onClick={handleMailClick}
                >
                  {userEmail}
                </span>
                . The files will be available in the Records queue after the email has been
                processed.
              </p>
            ) : (
              <>
                <p>
                  If you prefer you can send an email with attachments of all the files you would
                  like to upload. Those files will be available in the records queue once uploaded.
                </p>
                <p>
                  Then select if you would like to scan and have a document created based on the
                  image. You will find the successful scans and documents listed here in the Records
                  tab. Any issues when creating the document will be flagged as "Needs Review" or
                  "Failed" in the OCR Queue tab.
                </p>
                <p>
                  You can also direct your suppliers to email documents to{' '}
                  <span
                    className="link text-info font-semibold cursor-pointer whitespace-nowrap"
                    onClick={handleMailClick}
                  >
                    {userEmail}
                  </span>
                </p>
              </>
            )}
          </div>
          {deviceType === 'desktop' && (
            <div
              onClick={onSelectFilesClick}
              className="invoice-external-dropzone mb-[1px]"
              onKeyDown={(e) => e.key === 'Enter' && onSelectFilesClick()}
            >
              <ExternalDropZone
                uploadRef={uploadRef}
                customHint={hint}
                disabled={isUploadLoading || isUploadAndScanLoading}
              />
            </div>
          )}

          <Upload
            id="uploadInvoiceModal"
            ref={uploadRef}
            disabled={isUploadLoading || isUploadAndScanLoading}
            accept={isPDFOnly ? '.pdf' : '.pdf, image/jpg, image/jpeg, .gif, .bmp, image/png'}
            batch={false}
            multiple={true}
            defaultFiles={[]}
            withCredentials
            className="k-dropzone-hint"
            onAdd={onAdd}
            onRemove={handleRemoveFile}
            autoUpload={false}
            showActionButtons={false}
            listItemUI={UploadFileListItem}
          />
        </div>
        <DialogActionsBar>
          <div className="flex flex-row justify-end items-center mt-2">
            {isOcrCompany && (
              <PopUp
                popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
                anchorAlign={{ horizontal: 'center', vertical: 'top' }}
                content={
                  <div className="m-1 max-w-[300px]">
                    <PopUpContentWrapper type="Information" contents={[scanFilePopUpMessage]} />
                  </div>
                }
                show={isUploadAndScanDisabled}
              >
                <Button
                  color="info"
                  onClick={() => handleUploadAndScan(true)}
                  disabled={isUploadAndScanDisabled}
                  loading={isUploadAndScanLoading}
                >
                  Upload and Scan File(s)
                </Button>
              </PopUp>
            )}
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
              anchorAlign={{ horizontal: 'center', vertical: 'top' }}
              content={
                <div className="m-1 max-w-[300px]">
                  <PopUpContentWrapper
                    type="Information"
                    contents={['Please select a file to Upload.']}
                  />
                </div>
              }
              show={isUploadButtonDisable}
            >
              <Button
                color="info"
                disabled={isUploadButtonDisable || isUploadAndScanLoading}
                onClick={() => handleUploadAndScan(false)}
                loading={isUploadLoading}
              >
                Upload
              </Button>
            </PopUp>
            <Button
              color="info"
              onClick={closeModal}
              disabled={isUploadLoading || isUploadAndScanLoading}
            >
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Modal>
    </div>
  );
};

export default InvoiceUploadFilesModal;
